import React from 'react';
import { Calendar, Github, BarChart3, BookOpen, UtensilsCrossed, Scale, Microscope, Building2, Gamepad2, Languages, Trophy, Youtube, ExternalLink } from 'lucide-react';

const Projects = () => {
  const projectData = [
    {
      title: "NYC Property Scout",
      description: "Multi-agent AI system that helps NYC apartment seekers make informed decisions. An IBM WatsonX Orchestrate coordinator agent combines a Building Health Score (from 311 complaints data) and a Financial Quality Score (from NYC DOF tax data) into a Transparency Grade — plus an embedded chat for deeper property questions.",
      period: "Spring 2025",
      technologies: ["IBM WatsonX Orchestrate", "Multi-Agent AI", "NYC Open Data", "Python"],
      github: "https://github.com/LaxmanSRawat/NYC-Property-Scout",
      demo: "https://www.youtube.com/watch?v=dFMHC6LWsw4",
      badge: "🏆 1st Place — IBM Dev Day: AI Demystified",
      icon: <Building2 size={20} />,
    },
    {
      title: "Promptinary",
      description: "Competitive prompt-engineering game inspired by Pictionary. Players see a reference image and must recreate it using an AI prompt with as few words and as little time as possible — scored on image similarity, tokens used, and time taken. Includes power-ups (drain opponents' token budget, scramble their image) and per-round personalised feedback on what worked and why.",
      period: "Spring 2025",
      technologies: ["Next.js", "Google Cloud Platform", "Gemini Flash 2.5", "Vertex AI", "Imagen 3"],
      github: "https://github.com/anindaghosh/promptinary",
      demo: "https://www.youtube.com/shorts/T570c1UUzU4",
      badge: "🎖️ Top 8 of 50+ Teams — Google × Columbia Hackathon",
      icon: <Gamepad2 size={20} />,
    },
    {
      title: "Foreign Whispers",
      description: "End-to-end YouTube video dubbing pipeline: Download → Transcribe (Whisper) → Translate (argostranslate) → Synthesize (Chatterbox TTS with voice cloning) → Stitch (ffmpeg). Speaker diarization assigns per-speaker voices for natural multi-speaker interviews, while a duration-aware translation system and dynamic programming optimizer keep dubbed speech in sync. Outputs WebVTT rolling captions without re-encoding.",
      period: "Spring 2025",
      technologies: ["Python", "FastAPI", "Next.js", "Whisper", "pyannote", "Docker"],
      github: "https://github.com/LaxmanSRawat/foreign-whispers",
      demo: null,
      badge: null,
      icon: <Languages size={20} />,
    },
    {
      title: "Mealswipe",
      description: "Full-stack campus dining platform for NYU students. Features user authentication with rate-limited login, password reset via Gmail SMTP, and a React + TypeScript frontend backed by a Django REST API — deployed to AWS Elastic Beanstalk via a Travis CI/CD pipeline.",
      period: "Spring 2026",
      technologies: ["Django", "React", "TypeScript", "AWS Elastic Beanstalk", "Travis CI", "Vite"],
      github: "https://github.com/gcivil-nyu-org/team2-mon-spring26",
      demo: null,
      badge: null,
      icon: <UtensilsCrossed size={20} />,
    },
    {
      title: "Library Link",
      description: "Chromium browser extension that helps NYU students find library books while browsing Amazon. Features multi-persona support, book availability checking, savings tracker, and professor features.",
      period: "2025",
      technologies: ["JavaScript", "Chrome Extension", "HTML", "CSS"],
      github: "https://github.com/LaxmanSRawat/Library-Link",
      demo: null,
      badge: "⭐ Fan Favourite — NYU Hack Dibner 2025-26",
      icon: <BookOpen size={20} />,
    },
    {
      title: "NovaSight",
      description: "Interactive data visualization analyzing NYC 911 emergency call data for 2024, sourced from NYC Open Data. Features Observable notebooks for exploring incident patterns and trends.",
      period: "2025",
      technologies: ["Python", "Observable", "D3.js", "Data Visualization"],
      github: "https://github.com/LaxmanSRawat/NovaSight",
      demo: null,
      page: "https://laxmansrawat.github.io/NovaSight/",
      badge: null,
      icon: <BarChart3 size={20} />,
    },
    {
      title: "Responsible AI Research",
      description: "Academic research project for CS-9223 Responsible AI course at NYU, exploring ethical considerations and fairness in machine learning systems.",
      period: "2025",
      technologies: ["Python", "Machine Learning", "Fairness", "Ethics"],
      github: "https://github.com/Yahtze/CS-9223-RAI-Project",
      demo: null,
      badge: null,
      icon: <Scale size={20} />,
    },
    {
      title: "Failure Mode Classification in FRC",
      description: "Machine learning project classifying fiber failure modes in Fiber Reinforced Concrete using computer vision. Implemented 3 approaches: Bag of Visual Words (67%), Local Binary Pattern (89%), and custom CNN (90% accuracy).",
      period: "2019",
      technologies: ["Python", "TensorFlow", "OpenCV", "CNN"],
      github: null,
      demo: null,
      badge: null,
      icon: <Microscope size={20} />,
    }
  ];

  return (
    <section id="projects" className="section-padding bg-gsap-bg">
      <div className="container-max">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Projects</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {projectData.map((project, index) => (
            <div
              key={index}
              className="animate-on-scroll glass-card p-8"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Project Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-primary-500/10 border border-primary-500/30 flex items-center justify-center text-primary-500 flex-shrink-0">
                  {project.icon}
                </div>
                <div className="flex-grow">
                  <div className="flex items-start justify-between">
                    <h3 className="text-xl font-bold text-gsap-text leading-tight">
                      {project.title}
                    </h3>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      {project.page && (
                        <a
                          href={project.page}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View Live Page"
                          className="w-8 h-8 rounded-lg border border-gsap-border flex items-center justify-center text-gsap-muted hover:text-primary-500 hover:border-primary-500 transition-all duration-300"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                      {project.demo && (
                        <a
                          href={project.demo}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Watch Demo"
                          className="w-8 h-8 rounded-lg border border-gsap-border flex items-center justify-center text-gsap-muted hover:text-red-500 hover:border-red-500 transition-all duration-300"
                        >
                          <Youtube size={16} />
                        </a>
                      )}
                      {project.github && (
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View on GitHub"
                          className="w-8 h-8 rounded-lg border border-gsap-border flex items-center justify-center text-gsap-muted hover:text-primary-500 hover:border-primary-500 transition-all duration-300"
                        >
                          <Github size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gsap-muted mt-1">
                    <Calendar size={14} />
                    <span>{project.period}</span>
                  </div>
                </div>
              </div>

              {/* Award Badge */}
              {project.badge && (
                <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-primary-500/10 border border-primary-500/20">
                  <Trophy size={14} className="text-primary-500 flex-shrink-0" />
                  <span className="text-xs font-semibold text-primary-400 tracking-wide">
                    {project.badge}
                  </span>
                </div>
              )}

              {/* Description */}
              <p className="text-gsap-muted text-sm leading-relaxed mb-6">
                {project.description}
              </p>

              {/* Technologies */}
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-gsap-elevated text-gsap-muted rounded-full text-sm border border-gsap-border"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
